import type { Entity, World } from 'koota';
import { emit, getContext } from '../context';
import { Dead, Health, KillStreak, Player, RoundPhase, RoundState } from '../traits';
import { sameTeam } from './party';

export const KILL_STREAK_MILESTONES = [3, 5, 10] as const;
export type KillStreakProgress = { slot: number; current: number; best: number }[];

/** Resolve the whole tick before announcing a streak, so a trade never leaves a dead fighter on fire. */
export function updateKillStreaks(world: World): void {
  const ctx = getContext(world);
  const players = new Set(ctx.players);
  const deaths = new Set<Entity>();
  const credits = new Map<Entity, number>();
  const fighting = world.get(RoundState)?.phase === RoundPhase.Fighting;
  for (const event of ctx.events) {
    if (event.type !== 'kill') continue;
    const victim = event.target as Entity;
    if (!players.has(victim) || deaths.has(victim)) continue;
    deaths.add(victim);
    const attacker = event.source as Entity;
    if (
      !fighting ||
      !players.has(attacker) ||
      attacker === victim ||
      sameTeam(world, attacker, victim)
    )
      continue;
    credits.set(attacker, (credits.get(attacker) ?? 0) + 1);
  }

  for (const player of ctx.players) {
    const state = player.get(KillStreak) ?? { current: 0, best: 0 };
    const dead = player.has(Dead) || (player.get(Health)?.hp ?? 0) <= 0;
    // A lingering projectile may earn a KO after its owner died on an earlier tick. It cannot
    // begin a new life streak; a same-tick trade can still improve the life that just ended.
    const earned = !dead || deaths.has(player) ? (credits.get(player) ?? 0) : 0;
    const next = Math.min(Number.MAX_SAFE_INTEGER, state.current + earned);
    if (!player.has(KillStreak)) player.add(KillStreak());
    player.set(KillStreak, { current: dead ? 0 : next, best: Math.max(state.best, next) });
    if (dead) continue;
    for (const milestone of KILL_STREAK_MILESTONES) {
      if (state.current < milestone && next >= milestone)
        emit(world, { type: 'kill-streak', player, streak: milestone });
    }
  }
}

/** Slot identity survives a new world created to recover an online match. */
export function captureKillStreaks(world: World): KillStreakProgress {
  return getContext(world).players.map((player) => ({
    slot: player.get(Player)!.slot,
    ...(player.get(KillStreak) ?? { current: 0, best: 0 }),
  }));
}

export function restoreKillStreaks(world: World, progress: KillStreakProgress): void {
  const bySlot = new Map(progress.map((saved) => [saved.slot, saved]));
  for (const player of getContext(world).players) {
    const saved = bySlot.get(player.get(Player)!.slot);
    if (!player.has(KillStreak)) player.add(KillStreak());
    player.set(KillStreak, { current: saved?.current ?? 0, best: saved?.best ?? 0 });
  }
}
